import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { format, isSameDay, startOfMonth, startOfQuarter, startOfYear, subDays } from 'date-fns';
import { useListChampionshipsQuery } from 'modules/championship/store/championships-api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SingleValue } from 'react-select';
import UDDateInput from 'ud-form/components/datetime/component';
import UDFormLabel from 'ud-form/components/label';
import { UDSelect } from 'ud-form/components/select/component';
import { SelectOption } from 'ud-form/components/select/component/types';

interface AnalyticsFiltersProps {
  tournamentId: number;
  onFiltersChange: (filters: { championshipId?: number | null; startDate: string; endDate: string }) => void;
}

const DATE_PRESETS = [
  { label: msg`За последнюю неделю`, getStart: () => subDays(new Date(), 7) },
  { label: msg`За месяц`, getStart: () => startOfMonth(new Date()) },
  { label: msg`За квартал`, getStart: () => startOfQuarter(new Date()) },
  { label: msg`За год`, getStart: () => startOfYear(new Date()) },
] as const;

const AnalyticsFilters = React.memo(({ tournamentId, onFiltersChange }: AnalyticsFiltersProps) => {
  const { t } = useLingui();
  const allChampionshipsOption = useMemo<SelectOption<number | null>>(() => ({ label: t`Все чемпионаты`, value: null }), [t]);
  const [selectedChampionshipId, setSelectedChampionshipId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState<Date>(new Date());

  const { data: championships, isFetching: isChampionshipsFetching } = useListChampionshipsQuery({
    tournamentId,
    limit: 100,
    offset: 0,
    afterDate: format(startDate, 'yyyy-MM-dd'),
    beforeDate: format(endDate, 'yyyy-MM-dd'),
  });

  useEffect(() => {
    if (selectedChampionshipId === null) return;
    if (isChampionshipsFetching) return;
    const stillPresent = championships?.some((c) => c.id === selectedChampionshipId);
    if (!stillPresent) {
      setSelectedChampionshipId(null);
    }
  }, [championships, isChampionshipsFetching, selectedChampionshipId]);

  const championshipOptions = useMemo<SelectOption<number | null>[]>(() => {
    const items: SelectOption<number | null>[] = [allChampionshipsOption];
    championships?.forEach((c) => items.push({ label: c.name, value: c.id }));
    return items;
  }, [allChampionshipsOption, championships]);

  const selectedOption = useMemo(
    () => championshipOptions.find((o) => o.value === selectedChampionshipId) ?? allChampionshipsOption,
    [allChampionshipsOption, championshipOptions, selectedChampionshipId],
  );

  const handleChampionshipChange = useCallback((option: SingleValue<SelectOption<number | null>>) => {
    setSelectedChampionshipId(option?.value ?? null);
  }, []);

  const handleStartDateChange = useCallback((date: Date | null) => {
    if (!date) return;
    setStartDate(date);
    setSelectedChampionshipId(null);
  }, []);

  const handleEndDateChange = useCallback((date: Date | null) => {
    if (!date) return;
    setEndDate(date);
    setSelectedChampionshipId(null);
  }, []);

  const handlePresetClick = useCallback((getStart: () => Date) => {
    setStartDate(getStart());
    setEndDate(new Date());
    setSelectedChampionshipId(null);
  }, []);

  const activePresetIndex = useMemo(() => {
    const today = new Date();
    if (!isSameDay(endDate, today)) return -1;
    const ref = endDate;
    const expectedStarts = [subDays(ref, 7), startOfMonth(ref), startOfQuarter(ref), startOfYear(ref)];
    return expectedStarts.findIndex((expectedStart) => isSameDay(startDate, expectedStart));
  }, [startDate, endDate]);

  useEffect(() => {
    onFiltersChange({
      championshipId: selectedChampionshipId,
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
    });
  }, [selectedChampionshipId, startDate, endDate, onFiltersChange]);

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[260px]">
          <UDFormLabel className="mb-2">
            <Trans>Чемпионат</Trans>
          </UDFormLabel>
          <UDSelect options={championshipOptions} value={selectedOption} onChange={handleChampionshipChange} />
        </div>
        <div>
          <UDFormLabel className="mb-2">
            <Trans context="date range start">С</Trans>
          </UDFormLabel>
          <UDDateInput selected={startDate} onChange={handleStartDateChange} maxDate={endDate} />
        </div>
        <div>
          <UDFormLabel className="mb-2">
            <Trans context="date range end">По</Trans>
          </UDFormLabel>
          <UDDateInput selected={endDate} onChange={handleEndDateChange} minDate={startDate} maxDate={new Date()} />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-3">
        {DATE_PRESETS.map((preset, index) => {
          const isActive = activePresetIndex === index;
          return (
            <button
              key={preset.label.id}
              type="button"
              className={`cursor-pointer border-none bg-transparent p-0 text-sm underline transition-colors ${isActive ? 'text-accentNaplesYellow font-semibold' : 'text-black hover:text-gray-900'}`}
              onClick={() => handlePresetClick(preset.getStart)}
            >
              {t(preset.label)}
            </button>
          );
        })}
      </div>
    </div>
  );
});

export default AnalyticsFilters;
